import { type RefObject, useEffect, useRef } from 'react';

import type { Aabb2, Point2, ReadableStore } from '@frontend/viewer-core';

import { DIMENSION_X_COLOR, DIMENSION_Y_COLOR } from '../constants';
import { visibleBounds } from '../session/deriveModel';
import type { DrawingModel, OverlayState } from '../session/types';
import {
  type MeasurementFormat,
  formatLength,
} from '../utils/formatMeasurement';
import {
  type LabelledLine,
  type ToolViewport,
  bindOverlayFrame,
  createLabelledLine,
  placeLabelledLine,
  show,
} from '../utils/toolUtils';

/** Distance a dimension line stands off from the geometry it measures, in px. */
const DIMENSION_OFFSET_PX = 28;
/** How close a dimension line may sit to the edge of the viewport, in px. */
const DIMENSION_MARGIN_PX = 4;

const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

export type AxisDimensionsOverlayDeps = {
  model: ReadableStore<DrawingModel | null>;
  hiddenLayers: ReadableStore<ReadonlySet<number>>;
  overlays: ReadableStore<OverlayState>;
  viewport: ToolViewport;
  format: MeasurementFormat;
};

/**
 * Draws one dimension along the world edge it measures.
 *
 * `from`/`to` are the projected ends of that edge, so the line always lies on the
 * axis whose length it reports. The line is then pushed outwards along its own
 * normal, away from the part, and translated rigidly back inside the viewport if
 * that pushed it off — rigidly, so it stays parallel to the edge.
 */
const placeAxisDimension = (
  parts: LabelledLine,
  from: Point2,
  to: Point2,
  partCenter: Point2,
  size: { width: number; height: number },
  text: string,
): void => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy) || 1;

  let normalX = -dy / length;
  let normalY = dx / length;

  const midX = (from[0] + to[0]) / 2;
  const midY = (from[1] + to[1]) / 2;
  // Two normals face out of any edge; take the one pointing away from the part.
  if ((midX - partCenter[0]) * normalX + (midY - partCenter[1]) * normalY < 0) {
    normalX = -normalX;
    normalY = -normalY;
  }

  const shiftX =
    clamp(
      midX + normalX * DIMENSION_OFFSET_PX,
      DIMENSION_MARGIN_PX,
      size.width - DIMENSION_MARGIN_PX,
    ) - midX;
  const shiftY =
    clamp(
      midY + normalY * DIMENSION_OFFSET_PX,
      DIMENSION_MARGIN_PX,
      size.height - DIMENSION_MARGIN_PX,
    ) - midY;

  placeLabelledLine(
    parts,
    [from[0] + shiftX, from[1] + shiftY],
    [to[0] + shiftX, to[1] + shiftY],
    text,
  );
};

/**
 * Dimensions the part's overall width and height, each along its own world axis.
 *
 * The X dimension is drawn between the projections of the bounds' two bottom
 * corners and the Y dimension between its two left corners, so each line lies on
 * the axis whose length it reports — and both turn with the drawing.
 *
 * The earlier version spanned the *screen* extents instead while labelling them
 * with world extents. At no rotation the two agree, so it looked right; after a
 * quarter turn the horizontal line measured the part's height and still read out
 * its width, which showed up as the values never changing on rotate.
 */
const placeAxisDimensions = (
  bounds: Aabb2,
  toScreen: (world: Point2) => Point2,
  size: { width: number; height: number },
  format: MeasurementFormat,
  widthDimension: LabelledLine,
  heightDimension: LabelledLine,
): void => {
  const [minX, minY, maxX, maxY] = bounds;
  const bottomLeft = toScreen([minX, minY]);
  const bottomRight = toScreen([maxX, minY]);
  const topLeft = toScreen([minX, maxY]);
  const partCenter = toScreen([(minX + maxX) / 2, (minY + maxY) / 2]);

  placeAxisDimension(
    widthDimension,
    bottomLeft,
    bottomRight,
    partCenter,
    size,
    formatLength(maxX - minX, format),
  );
  placeAxisDimension(
    heightDimension,
    bottomLeft,
    topLeft,
    partCenter,
    size,
    formatLength(maxY - minY, format),
  );
};

/**
 * Overall width and height of the visible part, each along its own world axis.
 *
 * SVG rather than WebGL because the labels are real text. Imperative because it
 * redraws whenever the view moves — routing that through React would put a
 * commit on the pan path.
 */
export const useDxfAxisDimensionsOverlay = ({
  model,
  hiddenLayers,
  overlays,
  viewport,
  format,
}: AxisDimensionsOverlayDeps): RefObject<SVGSVGElement> => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const widthDimension = createLabelledLine(DIMENSION_X_COLOR);
    const heightDimension = createLabelledLine(DIMENSION_Y_COLOR);
    svg.appendChild(widthDimension.group);
    svg.appendChild(heightDimension.group);

    const draw = () => {
      const currentModel = model.getState();
      const { width, height } = viewport.size.getState();
      const bounds = visibleBounds(currentModel, hiddenLayers.getState());
      const showAxis = overlays.getState().axisDimensions && Boolean(bounds);

      show(widthDimension.group, showAxis);
      show(heightDimension.group, showAxis);
      if (!bounds || !showAxis) {
        return;
      }

      placeAxisDimensions(
        bounds,
        viewport.worldToScreen,
        { width, height },
        format,
        widthDimension,
        heightDimension,
      );
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        overlays.subscribe,
        hiddenLayers.subscribe,
        model.subscribe,
      ],
      onCleanup: () => svg.replaceChildren(),
    });
  }, [model, hiddenLayers, overlays, viewport, format]);

  return svgRef;
};
